import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { api } from 'services/api';
import { bindNotifier } from 'utils/notify';
import 'styles/components/notification-bar.css';

const AUTO_DISMISS_MS = 6000;

const NotificationBar = () => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [open, setOpen] = useState(false);
  const queueRef = useRef([]);

  const handleDismiss = async (notificationId) => {
    queueRef.current = queueRef.current.filter(x => x.id !== notificationId);
    setItems([...queueRef.current]);

    if (typeof notificationId === 'number' || (typeof notificationId === 'string' && !notificationId.includes('-'))) {
      try {
        await api.notifications.markAsRead(notificationId);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to mark notification as read', e);
      }
    }
  };

  const handleClearAll = async () => {
    queueRef.current = [];
    setItems([]);

    try {
      await api.notifications.markAllAsRead();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to mark all notifications as read', e);
    }
  };

  // Eksponér enqueue til notify.js
  useEffect(() => {
    const enqueue = (n) => {
      const id = `${Date.now()}-${Math.random()}`;
      setFeedback(previous => [{ id, ...n }, ...previous].slice(0, 2));
      // auto-dismiss
      setTimeout(() => {
        setFeedback(previous => previous.filter(x => x.id !== id));
      }, AUTO_DISMISS_MS);
    };
    bindNotifier(enqueue);
  }, []);

  // Hent ulæste notifikationer fra API når bruger skifter
  useEffect(() => {
    let alive = true;
    setOpen(false);
    setFeedback([]);
    setItems([]);
    queueRef.current = [];
    (async () => {
      if (!currentUser) { setItems([]); queueRef.current = []; return; }
      try {
        const list = await api.notifications.list({ user_id: currentUser.id, read: false, limit: 5 });
        if (!alive) return;
        const mapped = (list || []).map(n => ({
          id: n.id,
          instant_quote_id: n.instant_quote_id,
          type: n.type || 'info',
          message: n.message || n.data?.body || n.title || n.subject || 'Notifikation',
          data: n.data || {},
        }));
        queueRef.current = mapped.slice(0, 5);
        setItems([...queueRef.current]);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('notifications.list failed', e?.message || e);
        if (alive) { queueRef.current = []; setItems([]); }
      }
    })();
    return () => { alive = false; };
  }, [currentUser]);

  return (<>
    {items.length > 0 && <div className="notif-bar" role="region" aria-label="Notifikationer">
      <div className="notif-bar-inner" style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <button className="link" aria-expanded={open} onClick={() => setOpen(o => !o)}>
            {open ? 'Skjul' : 'Vis'} notifikationer ({items.length})
          </button>
          <button className="link" style={{ color: 'var(--neutral-600)', fontSize: '0.85rem' }} onClick={handleClearAll}>
            Ryd alle
          </button>
        </div>
        {open && (
          <ul className="notif-list">
            {items.map(n => (
              <li key={n.id} className={`notif ${n.type}`}>
                <strong className="type">{label(n.type)}</strong>
                <span className="msg">{n.message}</span>
                {ctaHref(n) && (
                  <Link className="btn small" to={ctaHref(n)} onClick={() => handleDismiss(n.id)}>Gå til</Link>
                )}
                <button
                  className="close"
                  aria-label="Luk"
                  onClick={() => handleDismiss(n.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>}
    <div className="notif-feedback" role="status" aria-live="polite" aria-atomic="false">
      {feedback.map(n => <div key={n.id} className={`notif ${n.type}`}>
        <strong className="type">{label(n.type)}</strong>
        <span className="msg">{n.message}</span>
      </div>)}
    </div>
    </>
  );
};

function label(t) {
  switch (t) {
    case 'success': return 'OK';
    case 'error': return 'Fejl';
    case 'rating': return 'Anmeldelse';
    case 'rating_request': return 'Afventende anmeldelse';
    case 'payment_pending': return 'Afventende betaling';
    case 'bid_new': return 'Nyt bud';
    case 'chat_new': return 'Ny besked';
    case 'credit_request': return 'Kreditanmodning';
    case 'credit_decision': return 'Kreditafgørelse';
    case 'instant_time_review': return 'Adminvurdering';
    default: return 'Info';
  }
}

function extractTaskId(n) {
  return (
    n?.data?.taskId || n?.data?.task_id || n?.taskId || n?.task_id || null
  );
}

function ctaHref(n) {
  // Construct only these known internal destinations; do not trust a stored URL.
  if(n.type==='instant_time_review'&&Number.isSafeInteger(Number(n.instant_quote_id||n.data?.instant_quote_id))&&Number(n.instant_quote_id||n.data?.instant_quote_id)>0)
    return n.data?.admin?'/admin?tab=instantQuote':`/task/instant/${Number(n.instant_quote_id||n.data.instant_quote_id)}`;
  if (n.type === 'credit_decision') return '/Profile?tab=credit';
  if (n.type === 'credit_request' && /^[0-9a-f-]{36}$/i.test(n.data?.creditRequestId || '')) {
    return `/admin?tab=businessUsers&creditRequest=${n.data.creditRequestId}`;
  }
  const taskId = extractTaskId(n);
  if (!taskId) return null;
  const t = n.type;
  if (t === 'rating' || t === 'rating_request') return `/task/${taskId}#review`;
  if (t === 'payment_pending') return `/task/${taskId}#payment`;
  if (t === 'bid_new') return `/task/${taskId}#bids`;
  if (t === 'chat_new') return `/task/${taskId}#chat`;
  return `/task/${taskId}`;
}

export default NotificationBar;
